import { createApi } from "@reduxjs/toolkit/query/react";
import { APP_CONFIG } from "../../constants/constants";
import { ListingResponse } from "../types/Listing";
import {
  BuzzerAlert,
  BuzzerAlertListingPayload,
  BuzzerAlertUpdatePayload,
  NotificationItem,
  NotificationListingPayload,
  NotificationSummary,
  NotificationSummaryPayload,
} from "../types/Notifications";
import { appBaseQuery, baseQueryWithAuthHandling } from "../utils/utils";
import { getSearchFieldsParams } from "./adminApiServices";
import { API_METHODS } from "./constants";
import { API_ENDPOINTS } from "./endpoints";

export enum NotificationApiTags {
  MARK_AS_READ = "markAsRead",
  MARK_ALL_AS_READ = "markAllAsRead",
  UPDATE_BUZZER_ALERT_STATUS = "clearBuzzerAlert",
}

export const NotificationAPIs = createApi({
  reducerPath: "notificationApi",
  tagTypes: [
    NotificationApiTags.MARK_AS_READ,
    NotificationApiTags.MARK_ALL_AS_READ,
    NotificationApiTags.UPDATE_BUZZER_ALERT_STATUS,
  ],
  baseQuery: (args, api, extraOptions) =>
    baseQueryWithAuthHandling(
      appBaseQuery.withAuthJson,
      args,
      api,
      extraOptions
    ),
  endpoints: (builder) => ({
    // Get event report for vehicle
    getNotificationList: builder.query<
      ListingResponse<NotificationItem[]>,
      NotificationListingPayload
    >({
      query: (params: NotificationListingPayload) => {
        return {
          url: API_ENDPOINTS.NOTIFICATIONS.GET_NOTIFICATIONS,
          method: API_METHODS.GET,
          params: params,
        };
      },
      providesTags: [
        NotificationApiTags.MARK_AS_READ,
        NotificationApiTags.MARK_ALL_AS_READ,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<NotificationItem[]>) => {
        return response;
      },
    }),

    // Dashboard count for notifications and alerts
    // --------------------------------------------
    // used for 1. token expiry check, and 2. polling for notifications and alerts
    // the NotificationSummaryPayload is added just for request purpose identification
    // this is not actually needed by the backend API
    getNotificationSummary: builder.query<
      NotificationSummary,
      NotificationSummaryPayload
    >({
      query: (params: NotificationSummaryPayload) => {
        return {
          url: API_ENDPOINTS.NOTIFICATIONS.NOTIFICATION_SUMMARY,
          method: API_METHODS.GET,
          params,
        };
      },
      providesTags: [
        NotificationApiTags.MARK_AS_READ,
        NotificationApiTags.MARK_ALL_AS_READ,
      ],
      transformErrorResponse(baseQueryReturnValue) {
        // disabling error reporting for summary API as this API gets polled at regular intervals
        // and sometimes when the system is on sleep and internet is not available, results in errors being displayed multiple times
        // handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: NotificationSummary) => {
        return response;
      },
    }),

    // Mark notification as read
    markNotificationAsRead: builder.mutation<any, number>({
      query: (id: number) => {
        return {
          url: API_ENDPOINTS.NOTIFICATIONS.MARK_AS_READ(id),
          method: API_METHODS.POST,
        };
      },
      invalidatesTags: [NotificationApiTags.MARK_AS_READ],
      transformErrorResponse(baseQueryReturnValue) {
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),

    // get buzzer alert list
    getBuzzerAlertList: builder.query<
      ListingResponse<BuzzerAlert[]>,
      BuzzerAlertListingPayload
    >({
      query: ({ search_fields, ...params }: BuzzerAlertListingPayload) => {
        return {
          url: API_ENDPOINTS.NOTIFICATIONS.BUZZER_ALERTS,
          method: API_METHODS.GET,
          params: {
            ...params,
            ...getSearchFieldsParams(
              search_fields,
              APP_CONFIG.ENTITY_SEARCH_FIELDS.BUZZER_ALERT_LISTING
            ),
          },
        };
      },
      providesTags: [NotificationApiTags.UPDATE_BUZZER_ALERT_STATUS],
      transformErrorResponse(baseQueryReturnValue) {
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<BuzzerAlert[]>) => {
        return response;
      },
    }),

    // update buzzer alert status
    updateBuzzerAlertStatus: builder.mutation<any, BuzzerAlertUpdatePayload>({
      query: ({ buzzerAlertId, ...restParams }: BuzzerAlertUpdatePayload) => {
        return {
          url: API_ENDPOINTS.NOTIFICATIONS.UPDATE_BUZZZER_ALERT_STATUS(
            buzzerAlertId
          ),
          method: API_METHODS.PATCH,
          body: restParams,
        };
      },
      invalidatesTags: [NotificationApiTags.UPDATE_BUZZER_ALERT_STATUS],
      transformErrorResponse(baseQueryReturnValue) {
        return baseQueryReturnValue;
      },
      transformResponse: (response: any) => {
        return response;
      },
    }),
  }),
});

export const {
  useGetNotificationListQuery,
  useGetNotificationSummaryQuery,
  useMarkNotificationAsReadMutation,
  useGetBuzzerAlertListQuery,
  useLazyGetBuzzerAlertListQuery,
  useUpdateBuzzerAlertStatusMutation,
} = NotificationAPIs;
